import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Modal,
  SidePanel,
  type BadgeVariant,
  type ButtonVariant,
} from '@silverfalls/sterling';
import { DetailRow } from '../components/DetailRow.js';
import { useToast } from '../components/Toast.js';
import {
  fetchApInvoice,
  legalNextInvoiceStates,
  resolveApInvoice,
  updateApInvoice,
  type APInvoiceLineVariance,
  type APInvoiceRow,
  type APInvoiceState,
  type UpdateAPInvoiceInput,
} from '../api/p2p.js';

const REQUESTING_USER = 'u-ui-skeleton';

export function APInvoiceDetail(): JSX.Element {
  const { ref } = useParams<{ ref: string }>();
  const toast = useToast();
  const [invoice, setInvoice] = useState<APInvoiceRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<APInvoiceState | null>(null);

  const load = useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchApInvoice(ref);
      setInvoice(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  const [pendingDestructive, setPendingDestructive] = useState<APInvoiceState | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const doResolve = useCallback(
    async (toState: APInvoiceState) => {
      if (!invoice) return;
      setResolving(toState);
      try {
        const result = await resolveApInvoice({
          invoiceRef: invoice.invoiceRef,
          toState,
          requestingUser: REQUESTING_USER,
        });
        if (result.__typename === 'ResolveAPInvoiceFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          toast.push(msg, 'error');
        } else {
          toast.push(`${result.fromState} → ${result.toState}`, 'success');
          await load();
        }
      } catch (err) {
        toast.push(err instanceof Error ? err.message : String(err), 'error');
      } finally {
        setResolving(null);
      }
    },
    [invoice, load, toast],
  );

  const onResolve = useCallback(
    async (toState: APInvoiceState) => {
      if (!invoice) return;
      // Destructive transitions get a Sterling type-to-confirm modal.
      // ON_HOLD is recoverable but still impactful (blocks payment) so
      // it also goes through the modal flow.
      if (toState === 'REJECTED' || toState === 'CANCELLED' || toState === 'ON_HOLD') {
        setPendingDestructive(toState);
        return;
      }
      await doResolve(toState);
    },
    [invoice, doResolve],
  );

  if (loading) return <div className="t-body-sm">Loading invoice…</div>;
  if (error) {
    return (
      <div className="alert alert-neg" role="alert">
        Couldn't load invoice: {error}
      </div>
    );
  }
  if (!invoice) {
    return (
      <div className="empty-state">
        <p>Invoice not found: {ref}</p>
        <p>
          <Link to="/ap-invoices">Back to invoices</Link>
        </p>
      </div>
    );
  }

  return (
    <section className="vendor-detail">
      <div className="screen-header">
        <div className="entity-header">
          <Link to="/ap-invoices" className="back-link">
            ← AP invoices
          </Link>
          <h2>{invoice.invoiceNumber}</h2>
          <div className="entity-ref mono">{invoice.invoiceRef}</div>
          <div className="entity-summary">
            <span className="entity-summary-part">
              <Badge variant={stateBadgeVariant(invoice.state)} dot={false}>
                {invoice.state}
              </Badge>
            </span>
            <span className="entity-summary-part">{kindLabel(invoice.kind)}</span>
            <span className="entity-summary-part">
              {invoice.vendor ? (
                <Link to={`/vendors/${invoice.vendorRef}`}>{invoice.vendor.legalName}</Link>
              ) : (
                '—'
              )}
            </span>
            <span className="entity-summary-part mono">
              {invoice.total} {invoice.currencyCode}
            </span>
            <span className="entity-summary-part mono">{formatDate(invoice.invoiceDate)}</span>
          </div>
        </div>
        <div className="header-right-stack">
          <div className="state-actions">
            <Button
              variant="secondary"
              size="sm"
              icon="pencil"
              onClick={() => setEditOpen(true)}
              disabled={resolving !== null}
            >
              Edit
            </Button>
            {legalNextInvoiceStates(invoice.state).map((next) => (
              <Button
                key={next}
                variant={actionVariant(next)}
                size="sm"
                onClick={() => onResolve(next)}
                disabled={resolving !== null}
                loading={resolving === next}
              >
                {actionLabel(next)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h3 className="detail-card-title">Header</h3>
          <div className="detail-rows">
            {invoice.sourceInvoiceRef && (
              <DetailRow
                label="Against invoice"
                value={
                  <Link
                    to={`/ap-invoices/${invoice.sourceInvoiceRef}`}
                    className="mono"
                  >
                    {invoice.sourceInvoiceRef}
                  </Link>
                }
              />
            )}
            <DetailRow label="OPCO" value={invoice.opcoRef} mono />
            <DetailRow label="Subtotal" value={invoice.subtotal} mono />
            <DetailRow label="Intake channel" value={invoice.intakeChannel} />
            <DetailRow label="Notes" value={invoice.notes ?? '—'} />
            <DetailRow label="Submitted by" value={invoice.createdBy} mono />
            <DetailRow label="Submitted at" value={formatDateTime(invoice.createdAt)} mono />
          </div>
        </div>
      </div>

      <div className="po-lines">
        <div className="po-lines-header">
          <span className="form-label">Lines ({invoice.lines.length})</span>
        </div>
        <table className="po-lines-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>PO line</th>
              <th>Qty</th>
              <th>Unit price</th>
              <th>Line total</th>
              <th>Variance</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line) => (
              <tr key={line.invoiceLineRef}>
                <td className="mono">{line.lineNumber}</td>
                <td>{line.description}</td>
                <td className="mono">
                  {line.poLineRef
                    ? `${line.poLineRef.slice(0, 16)}…`
                    : line.glAccount ?? '—'}
                </td>
                <td className="mono">{line.quantity}</td>
                <td className="mono">{line.unitPrice}</td>
                <td className="mono">{line.lineTotal}</td>
                <td>{renderVariance(line.variance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={pendingDestructive !== null}
        title={
          pendingDestructive === 'REJECTED'
            ? `Reject invoice ${invoice.invoiceNumber}`
            : pendingDestructive === 'CANCELLED'
              ? `Cancel invoice ${invoice.invoiceNumber}`
              : `Hold invoice ${invoice.invoiceNumber}`
        }
        variant={pendingDestructive === 'REJECTED' ? 'danger' : 'default'}
        confirmLabel={
          pendingDestructive === 'REJECTED'
            ? 'Reject invoice'
            : pendingDestructive === 'CANCELLED'
              ? 'Cancel invoice'
              : 'Hold invoice'
        }
        {...(pendingDestructive === 'REJECTED'
          ? { typeToConfirm: invoice.invoiceNumber }
          : {})}
        onCancel={() => setPendingDestructive(null)}
        onConfirm={() => {
          const next = pendingDestructive;
          setPendingDestructive(null);
          if (next) void doResolve(next);
        }}
      >
        {pendingDestructive === 'REJECTED' &&
          'Rejecting marks the invoice as terminal. The vendor will be notified; the invoice can\'t be paid. Type the invoice number to confirm.'}
        {pendingDestructive === 'CANCELLED' &&
          'Cancelling marks the invoice as voided. Use only when the invoice was issued in error. Type the invoice number to confirm.'}
        {pendingDestructive === 'ON_HOLD' &&
          'Putting the invoice on hold blocks payment until released. Use for under-investigation invoices. Type the invoice number to confirm.'}
      </Modal>

      {editOpen && (
        <EditAPInvoiceDialog
          invoice={invoice}
          onClose={() => setEditOpen(false)}
          onSaved={(changedFields) => {
            setEditOpen(false);
            if (changedFields.length === 0) toast.push('No changes', 'info');
            else toast.push(`Saved ${changedFields.length} change(s)`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

interface EditAPInvoiceDialogProps {
  readonly invoice: APInvoiceRow;
  readonly onClose: () => void;
  readonly onSaved: (changedFields: ReadonlyArray<string>) => void;
  readonly onError: (message: string) => void;
}

function EditAPInvoiceDialog({
  invoice,
  onClose,
  onSaved,
  onError,
}: EditAPInvoiceDialogProps): JSX.Element {
  const [notes, setNotes] = useState(invoice.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmitting(true);
      setError(null);

      const trimmed = notes.trim();
      const cur = invoice.notes ?? '';
      type MutableInput = {
        -readonly [K in keyof UpdateAPInvoiceInput]: UpdateAPInvoiceInput[K];
      };
      const working: MutableInput = {
        invoiceRef: invoice.invoiceRef,
        requestingUser: REQUESTING_USER,
      };
      if (trimmed !== cur) {
        if (trimmed === '') working.clear = ['notes'];
        else working.notes = trimmed;
      }

      try {
        const result = await updateApInvoice(working);
        if (result.__typename === 'UpdateAPInvoiceFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          setError(msg);
          onError(msg);
          setSubmitting(false);
          return;
        }
        onSaved(result.changedFields);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        onError(msg);
        setSubmitting(false);
      }
    },
    [invoice, notes, onSaved, onError],
  );

  return (
    <SidePanel
      open
      title="Edit invoice"
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Saving…' : 'Save changes'}
    >
      <form ref={formRef} className="form-grid" onSubmit={onSubmit}>
        <label className="form-row form-row-full">
          <span className="form-label">Notes</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes — empty to clear"
          />
        </label>
        {error && <div className="alert alert-neg" role="alert">{error}</div>}
      </form>
    </SidePanel>
  );
}

function renderVariance(v: APInvoiceLineVariance): JSX.Element {
  if (v.kind === 'NONE') {
    return <Badge variant="success" dot={false}>MATCH</Badge>;
  }
  if (v.kind === 'QTY_OVER') {
    return (
      <Badge variant="warning" dot={false} title={`invoice ${v.invoiceQty} vs received ${v.receivedQty}`}>
        QTY_OVER ({v.invoiceQty} vs {v.receivedQty})
      </Badge>
    );
  }
  if (v.kind === 'PRICE_DIFF') {
    return (
      <Badge variant="warning" dot={false} title={`invoice ${v.invoicePrice} vs PO ${v.poPrice}`}>
        PRICE_DIFF ({v.invoicePrice} vs {v.poPrice})
      </Badge>
    );
  }
  return (
    <Badge
      variant="danger"
      dot={false}
      title={`qty ${v.invoiceQty}/${v.receivedQty}; price ${v.invoicePrice}/${v.poPrice}`}
    >
      QTY+PRICE
    </Badge>
  );
}

function stateBadgeVariant(state: APInvoiceState): BadgeVariant {
  switch (state) {
    case 'MATCHED':
    case 'APPROVED':
      return 'success';
    case 'RECEIVED':
      return 'info';
    case 'VARIANCE_REVIEW':
    case 'ON_HOLD':
      return 'warning';
    case 'REJECTED':
    case 'CANCELLED':
      return 'neutral';
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toISOString().slice(0, 19).replace('T', ' ');
}


function kindLabel(kind: APInvoiceRow['kind']): string {
  switch (kind) {
    case 'PO_MATCHED':
      return 'PO-matched';
    case 'NON_PO':
      return 'Non-PO';
    case 'CREDIT_NOTE':
      return 'Credit note';
  }
}

function actionLabel(toState: APInvoiceState): string {
  switch (toState) {
    case 'APPROVED':
      return 'Approve';
    case 'REJECTED':
      return 'Reject';
    case 'ON_HOLD':
      return 'Hold';
    case 'VARIANCE_REVIEW':
      return 'Return to review';
    case 'CANCELLED':
      return 'Cancel';
    case 'MATCHED':
      return 'Mark matched';
    case 'RECEIVED':
      return 'Mark received';
  }
}

function actionVariant(toState: APInvoiceState): ButtonVariant {
  // One destructive action per group: REJECT is the vendor-facing kill
  // (the bill is being thrown back). CANCEL is an admin correction —
  // reversible-feeling, drops to secondary. ON_HOLD is recoverable.
  switch (toState) {
    case 'APPROVED':
    case 'MATCHED':
      return 'primary';
    case 'REJECTED':
      return 'destructive';
    case 'CANCELLED':
    case 'ON_HOLD':
    case 'VARIANCE_REVIEW':
    case 'RECEIVED':
      return 'secondary';
  }
}
